
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
    import Brieffelder from '@/components/Briefarten/Brieffelder.vue';
    import EingetragenAmVom from '@/components/EingetragenAmVom.vue';
    import Karteieintrag from '@/types/Karteieintrag';
    import moment from 'moment';
    import { SweetAlertResult } from 'sweetalert2';
    import { confirmSwal } from '@/helper/swals';
    import Briefart from '@/types/Briefart';
    import PatBrief from '@/types/PatBrief';
    import PatBriefInhalt from '@/types/PatBriefInhalt';
    import Brieffeld from '@/types/Brieffeld';
    import Patient from '@/types/Patient';
    import { ValidationRules } from '@/store/types';
    import { Resettable, VForm } from '@/types/Components';
    import { LoadBriefartenPayload } from '@/store/briefarten';
    import { LoadPersonenPayload } from '@/store/personen';

    @Component({
        components: {
            Brieffelder,
            EingetragenAmVom,
        },
        computed: {
            ...mapState('globals', ['rules', 'formatDate', 'parseDate']),
            ...mapState('session', ['arzt']),
            ...mapState('patient', ['patientData', 'loaders']),
            ...mapState('authentication', ['impfstelle_id']),
            ...mapState('briefarten', ['briefarten', 'platzhalter']),
            ...mapState('briefarten', { briefartenLoaders: 'loaders' }),
            ...mapGetters('personen', ['getPersonen']),
            ...mapState('personen', { personenLoaders: 'loaders' }),
        },
        methods: {
            ...mapMutations('session', ['setSelectedArzt']),
            ...mapActions('patient', [
                'updateBrief',
                'printBrief',
                'exportBrief',
                'deleteBrief',
                'loadImpfungen',
            ]),
            ...mapActions('briefarten', ['loadBriefarten']),
            ...mapActions('personen', ['loadPersonen']),
        },
    })
    export default class BriefDetail extends Vue {
        @Prop() visible!: boolean;
        @Prop() value!: Karteieintrag;

        public $refs!: {
            form: VForm;
            adresskopf: Vue;
            brieffelder: Resettable;
        };

        public rules!: ValidationRules;
        public patientData!: Patient | null;
        public briefarten!: Briefart[];
        public arzt!: number | null;
        public impfstelle_id!: number;
        public parseDate!: (input: string | null) => string | null;
        public formatDate!: (input: string | null) => string | null;
        public loadImpfungen!: () => void;
        public loadBriefarten!: (payload?: LoadBriefartenPayload) => void;
        public loadPersonen!: (payload?: LoadPersonenPayload) => void;
        public updateBrief!: (payload: { data: Partial<Karteieintrag>, print: boolean }) => void;
        public deleteBrief!: () => void;

        public brief = new PatBrief();
        public reloadImpfungen = false;
        public canChangeBriefart = false;

        public get isNew(): boolean {
            return this.brief.patbrief_id === null;
        }

        public get maxZeitraum(): string {
            return moment().format('YYYY-MM-DD');
        }

        @Watch('brief.briefart_id')
        public onChangeBriefart(briefart_id: number): void {
            if (!this.isNew && !this.canChangeBriefart) {
                this.canChangeBriefart = true;
                return;
            }

            const briefart = this.briefarten.find((briefart: Briefart) => briefart.briefart_id === briefart_id);

            if (briefart) {
                this.brief.briefinhalte = (new Array(Briefart.MAX_FIELDS))
                    .fill(undefined)
                    .map((_, index) => {
                        const feldnummer = index + 1;
                        const brieffeld =
                            briefart.brieffelder.find((brieffeld: Brieffeld) => brieffeld.feldnummer === feldnummer) ??
                            new Brieffeld(feldnummer);

                        const briefinhalt = PatBriefInhalt.from(brieffeld);

                        if (!this.isNew) {
                            const prevInhalt = this.brief.briefinhalte.find((briefinhalt: PatBriefInhalt) => {
                                return briefinhalt.feldnummer === feldnummer;
                            });

                            if (prevInhalt) {
                                briefinhalt.patbrief_id = prevInhalt.patbrief_id;
                                briefinhalt.patbriefinhalt_id = prevInhalt.patbriefinhalt_id;
                            }
                        }

                        return briefinhalt;
                    });
            }
        }

        @Watch('visible')
        public onChangeVisible(visible: boolean): void {
            if (!visible) {
                if (this.reloadImpfungen) {
                    this.loadImpfungen();
                }

                return;
            }

            this.reloadImpfungen = false;
            this.canChangeBriefart = false;

            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }

            if (this.$refs.adresskopf) {
                (this.$refs.adresskopf.$refs.input as HTMLInputElement).style.height = '';
            }

            if (this.$refs.brieffelder) {
                this.$refs.brieffelder.reset();
            }

            this.brief = new PatBrief(this.value.brief ?? undefined);

            this.loadBriefarten();

            if (this.isNew) {
                this.loadPersonen();
            }

            this.onClickPatientenadresse();

            if (this.isNew) {
                this.brief.datum = moment().format('YYYY-MM-DD');
                this.brief.patnr = this.patientData?.patnr ?? null;

                if (this.arzt) {
                    this.brief.person_id_arzt = this.arzt;
                }
            }

            this.datumErrorMessages = [];
            this.datumFormatted = this.formatDate(this.brief.datum);
        }

        @Watch('loaders.brief.save')
        public onChangeSave(saving: boolean): void {
            if (!saving && this.visible && !this.isNew) {
                this.brief = new PatBrief(this.value.brief ?? undefined);

                this.datumErrorMessages = [];
                this.datumFormatted = this.formatDate(this.brief.datum);
            }
        }

        public onClickPatientenadresse(): void {
            if (!this.patientData) {
                return;
            }

            this.brief.adresskopf = [
                this.patientData.name,
                this.patientData.strasse,
                [this.patientData.plz, this.patientData.ort, this.patientData.land_bezeichnung]
                    .filter((str) => str)
                    .join(' '),
            ].join('\r\n');
        }

        public onClickRechnungsadresse(): void {
            if (!this.patientData) {
                return;
            }

            this.brief.adresskopf = [
                this.patientData.rech_adrzeile1,
                this.patientData.rech_adrzeile2,
                this.patientData.rech_adrzeile3,
                this.patientData.rech_strasse,
                [this.patientData.rech_plz, this.patientData.rech_ort, this.patientData.rech_land_bezeichnung]
                    .filter((str) => str)
                    .join(' '),
            ].filter((str) => str).join('\r\n');
        }

        public onClickSave(print: boolean): void {
            if (!this.$refs.form.validate() || this.datumErrorMessages.length > 0) {
                return;
            }

            this.updateBrief({
                data: {
                    impfstelle_id: this.value.impfstelle_id ?? this.impfstelle_id,
                    patimpfung_id: this.value.patimpfung_id,
                    brief: this.brief,
                },
                print,
            });

            this.reloadImpfungen = !this.isNew;
        }

        public onClickDelete(): void {
            confirmSwal('Löschen', 'Brief endgültig löschen?', 'Löschen', 'Abbrechen')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        this.deleteBrief();
                    }
                });
        }

        public datumMenu = false;
        public datumFormatted: string | null = null;
        public datumErrorMessages: string[] = [];

        @Watch('brief.datum')
        public onChangeDatum(date: string | null): void {
            this.datumFormatted = this.formatDate(this.brief.datum);

            if (date !== null && this.isNew) {
                this.datumErrorMessages = [];

                for (const rule of [this.rules.required, this.rules.validDate]) {
                    const valid = rule(this.brief.datum);
                    if (valid !== true) {
                        this.datumErrorMessages.push(valid as string);
                    }
                }

                if (this.brief.datum && (this.brief.datum > moment().format('YYYY-MM-DD'))) {
                    this.datumErrorMessages.push('Datum darf nicht in der Zukunft sein.');
                }
            }
        }
    }
